import React, {
  createContext,
  FC,
  ReactNode,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
} from "react";
import LoadinAni from "./loadinAni";

type LoadingAniContextType = {
  playLoading: () => void;
};

const LoadingAniContext = createContext<LoadingAniContextType | undefined>(undefined);

type Props = {
  children: ReactNode;
};

const ANIMATION_DURATION = 6500;

export const LoadingAniProvider: FC<Props> = ({ children }) => {
  const [visible, setVisible] = useState(false);
  const hideTimerRef = useRef<number | null>(null);
  const startTimerRef = useRef<number | null>(null);

  const lockScroll = () => {
    document.body.style.overflow = "hidden";
  };

  const unlockScroll = () => {
    document.body.style.overflow = "";
  };

  const playLoading = useCallback(() => {
    setVisible(false);

    if (startTimerRef.current) {
      globalThis.window.clearTimeout(startTimerRef.current);
    }

    if (hideTimerRef.current) {
      globalThis.window.clearTimeout(hideTimerRef.current);
    }

    startTimerRef.current = globalThis.window.setTimeout(() => {
      lockScroll();
      setVisible(true);

      hideTimerRef.current = globalThis.window.setTimeout(() => {
        setVisible(false);
        unlockScroll();
      }, ANIMATION_DURATION);
    }, 20);
  }, []);

  const value = useMemo(
    () => ({
      playLoading,
    }),
    [playLoading]
  );

  return (
    <LoadingAniContext.Provider value={value}>
      {children}
      <LoadinAni visible={visible} />
    </LoadingAniContext.Provider>
  );
};

export const useLoadingAni = (): LoadingAniContextType => {
  const context = useContext(LoadingAniContext);

  if (!context) {
    throw new Error("useLoadingAni must be used within LoadingAniProvider");
  }

  return context;
};